<template>
	<div class="checkout-form__tip">
		<div
			v-if="options.length > 1"
			class="checkout-form__tip-buttons"
      :class="{
        'checkout-form__tip-buttons--grid': variant.grid,
        [`checkout-form__tip-buttons--grid-${variant.grid}`]: variant.grid
      }"
		>
			<label
				v-for="(option, index) in options"
				:key="index"
				class="checkout-form__tip-button"
        :class="{
          'checkout-form__tip-button--span': option.span,
        }"
			>
				<input
					class="checkout-form__tip-button-input"
					type="radio"
					:name="uniqueId"
					:value="index"
					v-model="selectedOptionIndex"
					:disabled="locked"
					@input="onSelect(option, index)"
				>
				<span
					class="checkout-form__tip-button-text"
					v-html="option.label"
				/>
			</label>
			<template v-if="selectedOption && selectedOption.options && selectedOption.options.length > 1">
				<label
					v-for="(option, index) in selectedOption.options"
					:key="`${selectedOptionIndex}-${index}`"
					class="checkout-form__tip-button"
					:class="{
						'checkout-form__tip-button--span': option.span,
					}"
				>
					<input
						class="checkout-form__tip-button-input"
						type="radio"
						:name="selectedOptionIndex + uniqueId"
						:value="index"
						v-model="selectedSubOptionIndex"
						:disabled="locked"
						@input="onSelect(option, selectedOptionIndex, index)"
					>
					<span
						class="checkout-form__tip-button-text"
						v-html="option.label"
					/>
				</label>
			</template>
		</div>
    <FormInputRange
			v-if="deepestSelectedOption && deepestSelectedOption.range"
			class="checkout-form__tip-input"
			:min="deepestSelectedOption.range.min || 0"
			:max="deepestSelectedOption.range.max || 100"
			:step="deepestSelectedOption.range.step || 1"
			v-model.number="rangeValue"
			hide-value
			:disabled="locked"
		/>
    <FormInputCurrency
      v-else-if="deepestSelectedOption && deepestSelectedOption.input"
      ref="subInput"
      class="checkout-form__tip-input"
      :placeholder="deepestSelectedOption.label"
      :label="deepestSelectedOption.label"
      :attributes="{
        min: 0,
        step: 0.05,
        ...getInputAttributes(deepestSelectedOption.input)
      }"
			v-model.number="inputValue"
      :disabled="locked"
    />
		<div
			v-if="selectedOption && selectedOption.type !== 'none'"
			class="checkout-form__tip-value"
		>
			<span
				v-if="selectedOption.type === 'percent'"
				class="checkout-form__tip-value-percent"
				v-html="`${tipPercent}%`"
			/>
			<span
				class="checkout-form__tip-value-currency"
				v-html="displayValue"
			/>
		</div>
		<div class="additional-info text--highlight"><span v-html="$t('cart.tipMessage')"/> <span v-if="tip" v-html="$t('cart.tipMessageThanks')"/></div>
	</div>
</template>

<script>
import FormInput from "@/components/FormInput"
import FormInputCurrency from "@/components/FormInputCurrency"
import FormInputRange from "@/components/FormInputRange"

export default {
	components: {
		FormInput,
		FormInputCurrency,
		FormInputRange,
	},
	props: {
		variant: Object,
    options: Array,
		locked: Boolean,
		selected: [String, Number],
		tip: {
			type: Number,
			default: 0
		},
		tipPercent: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			uniqueId: `tip-option-${(Date.now().toString(36) + Math.random().toString(36).substring(2, 7))}`,
			selectedOptionIndex: this.selected?.toString().split('-')[0],
			selectedSubOptionIndex: this.selected?.toString().split('-')[1],
			rangeValue: 0,
			inputValue: 0,
		}
	},
	computed: {
		selectedOption() {
			return this.options?.[this.selectedOptionIndex]
		},
		selectedSubOption() {
			return this.selectedOption?.options?.[this.selectedSubOptionIndex]
		},
		displayValue() {
			return this.selectedSubOption?.valueModified ?? this.selectedOption?.valueModified ?? this.$options.filters.fwgpricecent(this.tip, 'html')
		},
		deepestSelectedOption() {
			if (this.selectedOption?.options?.length) {
				return this.selectedSubOption
			}
			return this.selectedOption
		}
	},
	watch: {
		rangeValue(value) {
      const option = this.selectedSubOption ?? this.selectedOption

			if (option?.type === 'percent') {
				this.$emit('tip:percent', value)
			} else if (option?.type === 'chf') {
				this.$emit('tip:value', value)
			}
		},
		inputValue(value) {
      const option = this.selectedSubOption ?? this.selectedOption

			if (option?.type === 'percent') {
				this.$emit('tip:percent', value)
			} else if (option?.type === 'chf') {
				this.$emit('tip:value', value)
			}
		},
	},
	methods: {
		async onSelect(option, optionIndex, subOptionIndex) {
      const hasOptions = !!option.options?.length
      let selected = optionIndex

      if (subOptionIndex != null) {
        selected = `${optionIndex}-${subOptionIndex}`
      } else if (hasOptions) {
        selected = null
      }
			this.$emit('tip:option', selected)

      this.selectedOptionIndex = optionIndex
      this.selectedSubOptionIndex = subOptionIndex

      if (!hasOptions) {
        if (option.range) {
          this.rangeValue = 0
        }

        if (option.input) {
          this.inputValue = 0
        }

        if (option.value != null) {
          this.$emit('tip:value', option.value)
        } else if (option.percent != null) {
          this.$emit('tip:percent', option.percent)
        }
      }

      if (option.input) {
        await this.$nextTick()
        if (subOptionIndex != null) {
          this.$refs?.subInput?.focus()
        } else {
          this.$refs?.input?.focus()
        }
      }
		},
    getInputAttributes(attributes) {
      return Object.entries(attributes).reduce((a, [k,v]) => {
        if (['min', 'max', 'step'].includes(k)) {
          a[k] = v
        }
        return a
      }, {})
    }
	},
	mounted() {
    const selected = this.selected?.toString().split('-')
		this.options.forEach((option, index) => {
			if (selected?.length) {
				if (selected[0] === index) {
					this.selectedOptionIndex = index

          if (selected[1] != null && option.options?.length) {
            option.options.forEach((subOption, subIndex) => {
              if (selected[1] === subIndex) {
								this.selectedSubOptionIndex = subIndex
                this.onSelect(subOption, index, subIndex)
              }
            })
          } else {
            this.onSelect(option, index)
          }
				}
			}
			else if (option.default) {
				this.selectedOptionIndex = index
        const subIndex = option.options?.findIndex(o => o.default)

        if (subIndex >= 0) {
          this.onSelect(option.options[subIndex], index, subIndex)
        } else {
          this.onSelect(option, index)
        }
			}
		})
    if (this.options.length === 1) {
      const option = this.options[0]

      if (option.options?.length === 1) {
        this.onSelect(option.options[0], 0, 0)
      } else {
        this.onSelect(option, 0)
      }
    }
	}
}
</script>
